import { createContext, useContext } from "react";
import { Agent, LogEntry, InboxItem, TimelineEvent, TerminalTheme } from "./types";

/**
 * Shared application state, threaded to every route page via React context.
 *
 * Everything that used to live as closure scope inside the App() god-component
 * now hangs off this context object. The AppShell owns the *single* source of
 * truth (SSE connections, polling/refresh, selected-agent scope, theme,
 * capabilities) and publishes it here; lazy-loaded page components read it with
 * `useAppContext()`.
 *
 * Why context and not props: the page set is large + lazy, and React Router's
 * <Outlet/> can't forward props cleanly to descendants. Context keeps the
 * page signatures stable (no prop-drilling through the router) and guarantees
 * every page reads the *same* scopedFetch/agent/theme the shell mounted.
 */
export interface AppContextValue {
  // Agents + scope
  agents: Agent[];
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string | null) => void;
  selectedAgent: Agent | null | undefined;
  scopedAgentId: string | null;
  isSingleAgentMode: boolean;
  isFleetOverview: boolean;

  // Live data (fed by SSE + refreshAllState, mounted once in the shell)
  logs: LogEntry[];
  inbox: InboxItem[];
  timeline: TimelineEvent[];
  hardeningAudit: any;
  refreshAllState: () => Promise<void> | void;

  // Networking — fleetClient-scoped fetch (agentScopedUrl under the hood).
  scopedFetch: (path: string, init?: RequestInit) => Promise<Response>;

  // SSE connection state — "live" | "reconnecting" | "stale". Owned by the
  // shell (it holds the EventSource handles); pages read it for freshness UI.
  connState: "live" | "reconnecting" | "stale";

  // Theme
  theme: TerminalTheme;
  selectedThemeId: string;
  setSelectedThemeId: (id: string) => void;

  // Capabilities gating
  canDo: (action: string) => boolean;

  // Cross-page actions
  onToggleStatus: (agentId: string, currentStatus: string) => Promise<void>;
  onTriggerInbound: (payload: any) => Promise<boolean>;

  // Navigation helper for legacy goToTab(tabId) call sites — maps a tab id to
  // a route path and navigates there.
  goToTab: (tabId: string) => void;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useAppContext must be used within <AppContext.Provider> (AppShell)");
  }
  return ctx;
}
